import { createSelector } from 'reselect';
import { ClientReducerState } from 'client/reducers';
import { selectCardId } from 'client/reducers/card/selectors';
import type { AnswerInputContent } from './types';

export const selectUser = (state: ClientReducerState) => state.user;

export const selectUserCountry = createSelector(selectUser, user => user.country);

export const selectUserAnswers = createSelector(selectUser, user => user.answers);

export const selectUserAnswersByCard = createSelector(
	[selectUserAnswers, (state: ClientReducerState, cardId?: string) => cardId ?? selectCardId(state)],
	(answers, cardId) => (answers !== undefined && cardId !== undefined ? answers[cardId] : undefined)
);

export const selectTotalAnswersByCard = createSelector(
	(state: ClientReducerState, cardId?: string) => selectUserAnswersByCard(state, cardId ?? selectCardId(state)),
	answers => answers?.selected.length ?? 0
);

export const selectAnswerInputDataByCard = createSelector(
	(state: ClientReducerState, cardId?: string) => selectUserAnswersByCard(state, cardId ?? selectCardId(state)),
	answers => {
		if (!answers?.data) return null;

		// we assume there might be only one answer-input per card,
		// so taking the first answer that has 'attempts'
		const answerData = Object.values(answers.data).find(
			(a): a is AnswerInputContent => a?.type === 'answerInput' && a?.inputData?.attempts !== undefined
		);
		return answerData?.inputData;
	}
);

export const selectAnswerAttemptByCard = createSelector(
	(state: ClientReducerState, cardId?: string) => selectAnswerInputDataByCard(state, cardId ?? selectCardId(state)),
	answerInputData => answerInputData?.attempts ?? 0
);

export const selectAnswerResultByCard = createSelector(
	(state: ClientReducerState, cardId?: string) => selectAnswerInputDataByCard(state, cardId ?? selectCardId(state)),
	answerInputData => Boolean(answerInputData?.result)
);
